import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { scanProducts, pickProduct, formatPrice, type ScannedProduct } from '../utils/productScanner';
import { BUYER_NAMES, US_CITIES } from '../data/socialProofData';
import { CartIcon, CheckIcon } from './icons/Icons';
type Toast = {
  id: number;
  name: string;
  city: string;
  product: ScannedProduct;
};
const FIRST_APPEARANCE_AT = 3000;
const MIN_GAP = 8000;
const MAX_GAP = 15000;
const VISIBLE_MS = 4000;
const NAME_HISTORY = 10;
function randomItem<T>(list: T[]): T {
  return list[Math.floor(Math.random() * list.length)];
}

/**
 * "Recent Purchase" social-proof toast system. Scans the page for products
 * on mount, then shows one toast at a time on a randomized schedule.
 */
export function PurchaseNotifications() {
  const [current, setCurrent] = useState<Toast | null>(null);
  const catalogRef = useRef<ScannedProduct[]>([]);
  const recentNamesRef = useRef<string[]>([]);
  const idRef = useRef(0);
  const timers = useRef<number[]>([]);
  const reduceMotion = useReducedMotion();
  useEffect(() => {
    const clearAll = () => {
      timers.current.forEach(t => window.clearTimeout(t));
      timers.current = [];
    };
    const pickName = (): string => {
      const history = recentNamesRef.current;
      const available = BUYER_NAMES.filter(n => !history.includes(n));
      const name = available.length > 0 ? randomItem(available) : randomItem(BUYER_NAMES);
      history.push(name);
      if (history.length > NAME_HISTORY) history.shift();
      return name;
    };
    const showNext = () => {
      if (catalogRef.current.length === 0) {
        catalogRef.current = scanProducts();
      }
      const toast: Toast = {
        id: ++idRef.current,
        name: pickName(),
        city: randomItem(US_CITIES),
        product: pickProduct(catalogRef.current)
      };
      setCurrent(toast);

      // Hide after visible window, then schedule the following toast.
      const hideTimer = window.setTimeout(() => {
        setCurrent(null);
        const gap = MIN_GAP + Math.random() * (MAX_GAP - MIN_GAP);
        const nextTimer = window.setTimeout(showNext, gap);
        timers.current.push(nextTimer);
      }, VISIBLE_MS);
      timers.current.push(hideTimer);
    };

    // Preserve the first appearance at ~3s from navigation even though this
    // non-critical chunk is intentionally mounted after the first paint.
    const elapsed = window.performance?.now?.() ?? 0;
    const firstDelay = Math.max(0, FIRST_APPEARANCE_AT - elapsed);
    const firstTimer = window.setTimeout(showNext, firstDelay);
    timers.current.push(firstTimer);
    return clearAll;
  }, []);
  const enter = reduceMotion ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 1,
    y: 0
  };
  const from = reduceMotion ? {
    opacity: 0,
    y: 0
  } : {
    opacity: 0,
    y: 40
  };
  const exit = reduceMotion ? {
    opacity: 0,
    y: 0
  } : {
    opacity: 0,
    y: 40
  };
  return <div className="pointer-events-none fixed inset-x-0 bottom-4 z-[60] flex justify-center px-4" aria-live="polite" aria-atomic="true" data-id="element-345" data-mp="2n550ce6ucdlr">
      <AnimatePresence data-id="element-346" data-mp="1zgoxhe5iu5oo">
        {current && <motion.div key={current.id} initial={from} animate={enter} exit={exit} transition={{
        duration: reduceMotion ? 0.15 : 0.45,
        ease: [0.22, 1, 0.36, 1]
      }} className="pointer-events-auto flex w-full max-w-sm items-center gap-3 rounded-2xl border border-black/5 bg-white p-3 shadow-xl" role="status" data-id="element-347" data-mp="1zi03zlqcprjh">
            <div className="relative shrink-0" data-id="element-348" data-mp="2icoreui27jaw">
              {current.product.thumbnailUrl ? <img src={current.product.thumbnailUrl} alt="" width="48" height="48" loading="lazy" decoding="async" className="h-12 w-12 rounded-lg object-cover" data-id="element-349" data-mp="2w7qvrd79d0ll" /> : <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-[#3f6b3a]/10 text-[#3f6b3a]" data-id="element-350" data-mp="3l7nacow9j1s4">
                  <CartIcon className="h-6 w-6" strokeWidth={2} data-id="element-351" data-mp="3p7npycv3lj0r" />
                </span>}
              <span className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-[#3f6b3a] text-white ring-2 ring-white" data-id="element-352" data-mp="3u0qjwm3seuhh">
                <CheckIcon className="h-3 w-3" strokeWidth={3.2} data-id="element-353" data-mp="328j23icjpc1i" />
              </span>
            </div>

            <div className="min-w-0 flex-1" data-id="element-354" data-mp="369l241eads4j">
              <p className="truncate text-sm text-[#1f2a22]" data-id="element-355" data-mp="36acz9gnj7xwx">
                <span className="font-semibold" data-id="element-356" data-mp="3b8efzuxhjl58">{current.name}</span>
                <span className="text-[#5a6b5c]" data-id="element-357" data-mp="3bbaebkd8wbu7"> en {current.city}</span>
              </p>
              <p className="truncate text-sm text-[#3d4a40]" data-id="element-358" data-mp="3g5o4n8d1vss0">
                acaba de comprar{' '}
                <span className="font-semibold text-[#1f2a22]" data-id="element-359" data-mp="ql2xrv741jm7">
                  {current.product.editionLabel}
                </span>{' '}
                — <span className="font-bold text-[#e0611f]" data-id="element-360" data-mp="2g03wma0i038s">{formatPrice(current.product)}</span>
              </p>
              <p className="mt-0.5 text-[10px] font-medium uppercase tracking-wide text-[#9aa79c]" data-id="element-361" data-mp="2b22djp32o3xb">
                Compra verificada · hace unos segundos
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}